import React, { useEffect, useState } from "react";
import FaireContext from "../contexts/faire-context";
import SiteContext from "../contexts/site-context";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import Site from "../models/site";
import SubscriptionContext from "../contexts/subscription-context";
import Subscription from "../models/subscription";
import PageLoader from "../components/common/PageLoader";
import Subscribe from "../components/Subscription";
import ConnectFaire from "../components/features/onboarding/ConnectFaire";
import Hotjar from "@hotjar/browser";
import { hotjarConfig } from "../config/hotjar";
import StoreNotInstalledAlert from "../components/warnings/StoreNotInstalledAlert";

export const queryClient = new QueryClient();

export function withProviders(Component: React.ComponentType) {
  return function () {
    /*
    |--------------------------------------------------------------------------
    | Hotjar Implementation
    |--------------------------------------------------------------------------
    |
    */
    const mySiteID = hotjarConfig.siteId;
    const hotjarVersion = hotjarConfig.version;

    Hotjar.init(mySiteID, hotjarVersion);

    /*
    |--------------------------------------------------------------------------
    | Site details
    |--------------------------------------------------------------------------
    |
    */
    const [siteId, setSiteId] = useState("");
    const [wixStoreInstalled, setWixStoreInstalled] = useState<boolean>(false);

    /*
    |--------------------------------------------------------------------------
    | Last Synced Details
    |--------------------------------------------------------------------------
    |
    */
    const [faireProductSync, setFaireProductSync] = useState("");
    const [faireOrderSync, setFaireOrderSync] = useState("");
    const [wixProductSync, setWixProductSync] = useState("");

    /*
    |--------------------------------------------------------------------------
    | Subscription details
    |--------------------------------------------------------------------------
    |
    */

    const [numberOfTrialDays, setNumberOfTrialDays] = useState(7);
    const [isPaid, setIsPaid] = useState(false);
    const [isTrialStarted, setIsTrialStarted] = useState(false);
    const [isTrialExpired, setIsTrialExpired] = useState(false);
    const [isExpired, setIsExpired] = useState(false);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [formattedTrialExpiryDate, setFormattedTrialExpiryDate] = useState("");
    const [formattedExpiryDate, setFormattedExpiryDate] = useState(null);

    /*
    |--------------------------------------------------------------------------
    | Faire connection
    |--------------------------------------------------------------------------
    |
    */

    const [isFaireConnected, setIsFaireConnected] = useState(true);
    const [isFaireCurrencySet, setIsFaireCurrencySet] = useState(true);
    const [isInventorySettingSet, setIsInventorySettingSet] = useState(true);
    const [isOrderSettingSet, setIsOrderSettingSet] = useState(true);
    const [isCurrencyMatched, setIsCurrencyMatched] = useState(true);
    const [isConversionSet, setIsConversionSet] = useState(true);
    const [isFetchingSiteDetails, setIsFetchingSiteDetails] = useState(true);

    /*
    |--------------------------------------------------------------------------
    | Flags
    |--------------------------------------------------------------------------
    |
    */

    const [shouldShowLoading, setShouldShowLoading] = useState(true);
    const [shouldShowPricing, setShouldShowPricing] = useState(false);
    const [shouldShowOnboarding, setShouldShowOnboarding] = useState(false);
    const [shouldShowMainContent, setShouldShowMainContent] = useState(false);
    const [shouldShowWixStoreNotInstalled, setShouldShowWixStoreNotInstalled] = useState(false);

    // Helpscout Integration
    React.useEffect(() => {
      const script = document.createElement("script");
      const secondScript = document.createElement("script");

      script.innerHTML = `!function(e,t,n){function a(){var e=t.getElementsByTagName("script")[0],n=t.createElement("script");n.type="text/javascript",n.async=!0,n.src="https://beacon-v2.helpscout.net",e.parentNode.insertBefore(n,e)}if(e.Beacon=n=function(t,n,a){e.Beacon.readyQueue.push({method:t,options:n,data:a})},n.readyQueue=[],"complete"===t.readyState)return a();e.attachEvent?e.attachEvent("onload",a):e.addEventListener("load",a,!1)}(window,document,window.Beacon||function(){});`;

      secondScript.innerHTML = `window.Beacon('init', 'a743fa21-44ba-4765-a26b-fc74aaf9bfdc')`;

      // Append script to head
      document.head.appendChild(script);
      document.head.appendChild(secondScript);
    }, []);

    useEffect(() => {
      setIsFetchingSiteDetails(true);
      Site.details()
        .then((response: any) => {
          setSiteId(response.site_id);
          setIsPaid(response.is_paid);
          setIsTrialStarted(response.is_trial_started);
          setIsTrialExpired(response.is_trial_expired);
          setIsExpired(response.is_expired);
          setCurrentPlan(response.current_plan);
          setIsFaireConnected(response.is_faire_connected);
          setIsFaireCurrencySet(response.is_faire_currency_set);
          setIsInventorySettingSet(response.is_inventory_setting_set);
          setIsOrderSettingSet(response.is_order_setting_set);
          setIsCurrencyMatched(response?.faire_currency === response?.wix_currency ? true : false);
          setIsConversionSet(response.currency_conversion_rate);
          setFormattedTrialExpiryDate(response.formatted_trial_expiry_date);
          setFormattedExpiryDate(response.formatted_expiry_date);
          setFaireProductSync(response?.last_faire_product_sync);
          setFaireOrderSync(response?.last_faire_order_sync);
          setWixProductSync(response?.last_wix_product_sync);
          setWixStoreInstalled(response?.is_wix_store_installed);
        })
        .catch((error) => {
          setSiteId("");
          setIsPaid(false);
          setIsTrialStarted(false);
          setIsTrialExpired(false);
          setIsExpired(false);
          setCurrentPlan(null);
          setIsFaireConnected(false);
          setIsFaireCurrencySet(false);
          setIsInventorySettingSet(false);
          setIsOrderSettingSet(false);
          setIsCurrencyMatched(false);
          setIsConversionSet(false);
          setFormattedTrialExpiryDate("");
          setFaireProductSync("");
          setFaireOrderSync("");
          setWixProductSync("");
          setWixStoreInstalled(false);
        })
        .finally(() => {
          setIsFetchingSiteDetails(false);
        });
    }, []);

    useEffect(() => {
      Subscription.getSettings().then((response: any) => {
        setNumberOfTrialDays(response.data.number_of_trial_days);
      });
    }, []);

    useEffect(() => {
      setShouldShowLoading(isFetchingSiteDetails);
    }, [isFetchingSiteDetails]);

    useEffect(() => {
      setShouldShowPricing(
        (!isTrialStarted || isTrialExpired) &&
          (!isPaid) &&
          !isFetchingSiteDetails &&
          !shouldShowWixStoreNotInstalled
      );
    }, [isTrialStarted, isTrialExpired, isPaid, isFetchingSiteDetails, shouldShowWixStoreNotInstalled]);

    useEffect(() => {
      setShouldShowOnboarding(
        (!isFaireConnected || !isFaireCurrencySet || !isInventorySettingSet || !isOrderSettingSet) &&
          !shouldShowPricing &&
          !shouldShowWixStoreNotInstalled
      );
    }, [
      isFaireConnected,
      isFaireCurrencySet,
      isInventorySettingSet,
      isOrderSettingSet,
      shouldShowPricing,
      shouldShowWixStoreNotInstalled,
    ]);

    useEffect(() => {
      if (!shouldShowLoading && !shouldShowPricing && !shouldShowOnboarding && !shouldShowWixStoreNotInstalled) {
        setShouldShowMainContent(true);
      }
    }, [
      shouldShowLoading,
      shouldShowPricing,
      shouldShowOnboarding,
      shouldShowWixStoreNotInstalled,
      shouldShowWixStoreNotInstalled,
    ]);

    // useEffect(() => {
    //   if (!wixStoreInstalled && !isFetchingSiteDetails) {
    //     setShouldShowWixStoreNotInstalled(true);
    //   }
    // }, [wixStoreInstalled, !isFetchingSiteDetails]);

    return (
      <FaireContext.Provider
        value={{
          isFaireConnected: isFaireConnected,
          setIsFaireConnected: setIsFaireConnected,
          isFaireCurrencySet: isFaireCurrencySet,
          setIsFaireCurrencySet: setIsFaireCurrencySet,
          isInventorySettingSet: isInventorySettingSet,
          setIsInventorySettingSet: setIsInventorySettingSet,
          isOrderSettingSet: isOrderSettingSet,
          setIsOrderSettingSet: setIsOrderSettingSet,
          isCurrencyMatched: isCurrencyMatched,
          setIsCurrencyMatched: setIsCurrencyMatched,
          isConversionSet: isConversionSet,
          setIsConversionSet: setIsConversionSet,
          isFetching: isFetchingSiteDetails,
          setIsFetching: setIsFetchingSiteDetails,
        }}
      >
        <SiteContext.Provider
          value={{
            siteId: siteId,
            setSiteId: setSiteId,
            faireProductSync: faireProductSync,
            setFaireProductSync: setFaireProductSync,
            faireOrderSync: faireOrderSync,
            setFaireOrderSync: setFaireOrderSync,
            wixProductSync: wixProductSync,
            setWixProductSync: setWixProductSync,
          }}
        >
          <SubscriptionContext.Provider
            value={{
              numberOfTrialDays: numberOfTrialDays,
              setNumberOfTrialDays: setNumberOfTrialDays,
              isPaid: isPaid,
              setIsPaid: setIsPaid,
              isTrialStarted: isTrialStarted,
              setIsTrialStarted: setIsTrialStarted,
              isTrialExpired: isTrialExpired,
              setIsTrialExpired: setIsTrialExpired,
              isExpired: isExpired,
              setIsExpired: setIsExpired,
              currentPlan: currentPlan,
              setCurrentPlan: setCurrentPlan,
              formattedTrialExpiryDate: formattedTrialExpiryDate,
              setFormattedTrialExpiryDate: setFormattedTrialExpiryDate,
              formattedExpiryDate: formattedExpiryDate,
              setFormattedExpiryDate: setFormattedExpiryDate,
            }}
          >
            <QueryClientProvider client={queryClient}>
              {shouldShowLoading && <PageLoader />}
              {shouldShowPricing && <Subscribe />}
              {shouldShowOnboarding && <ConnectFaire />}
              {shouldShowMainContent && <Component />}
              {shouldShowWixStoreNotInstalled && <StoreNotInstalledAlert />}
              <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>
          </SubscriptionContext.Provider>
        </SiteContext.Provider>
      </FaireContext.Provider>
    );
  };
}
